import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { subDays } from 'date-fns'

import { OnboardingFlow } from '@/pages/dashboard/onboarding/OnboardingFlow.jsx'

import { useAuthContext } from '@/context-state/AuthContext'
import { useMetaConnection } from '@/hooks/useMetaConnection'
import { useRelevantIntegration } from '@/hooks/useRelevantIntegration.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace'
import { useCurrentAccount } from '@/hooks/useCurrentAccount'
import { useAdsData } from '@/hooks/useAdsData'

import * as API from '@/services/api'
import { handleError } from '@/services/errorHandler'
import { AnalyticsService } from '@/services/analytics'
import { IntegrationProviders } from '@/consts'

// Personal-email providers we never treat as a company website. When the user's
// email domain is one of these we can't infer a brand, so the flow reveals an
// optional website field instead.
const GENERIC_EMAIL_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'ymail.com',
    'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'msn.com',
    'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me', 'protonmail.com',
    'pm.me', 'gmx.com', 'gmx.net', 'zoho.com', 'yandex.com', 'mail.com',
    'fastmail.com', 'hey.com', 'duck.com', 'qq.com', '163.com',
])

const inferDomainFromEmail = (email) => {
    const domain = email?.split('@')[1]?.toLowerCase().trim()
    if (!domain || GENERIC_EMAIL_DOMAINS.has(domain)) {
        return null
    }
    return domain
}

const normalizeUrl = (url) => {
    const trimmed = (url || '').trim()
    if (!trimmed) {
        return ''
    }
    return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`
}

export default function OnboardingPage() {

    const [isMetaConnectionStarted, setIsMetaConnectionStarted] = useState(false)
    const [isFinishing, setIsFinishing] = useState(false)
    const isSyncTriggeredRef = useRef(false)

    const { account } = useCurrentAccount()
    const { connectToMeta } = useMetaConnection()
    const { space } = useCurrentSpace()
    const { logout, user, updateAccountRequiresOnboarding } = useAuthContext()
    const { integration: metaIntegration } = useRelevantIntegration(IntegrationProviders.META)
    const navigate = useNavigate()
    const { fetchAdReportsData } = useAdsData(() => { })

    const inferredDomain = inferDomainFromEmail(user?.email)
    const firstName = user?.name?.split(' ')[0] || ''

    const triggerAsyncSyncOfMetaAdAccount = async (adAccountsToTriggerSync, isFirstAdAccount = false) => {
        try {
            AnalyticsService.track('trigger-meta-sync-job-from-onboarding', { adAccountsToTriggerSync })
            await API.triggerMetaSyncJob(metaIntegration._id, adAccountsToTriggerSync)
            if (isFirstAdAccount) {
                fetchAdReportsData({ from: subDays(new Date(), 7), to: new Date() }, false, undefined, true)
                fetchAdReportsData({ from: subDays(new Date(), 14), to: subDays(new Date(), 7) }, false, undefined, true)
                fetchAdReportsData({ from: subDays(new Date(), 14), to: new Date() }, false, undefined, true)
            }
        } catch (e) {
            handleError(e)
        }
    }

    const triggerSyncOnce = () => {
        const adAccounts = space.adAccounts?.length ? space.adAccounts : metaIntegration.adAccounts
        const adAccountsIdsToTriggerSync = adAccounts?.length ? adAccounts.slice(0, 5).map((adAccount) => adAccount.id) : []

        if (!isSyncTriggeredRef.current && adAccountsIdsToTriggerSync.length) {
            isSyncTriggeredRef.current = true
            adAccountsIdsToTriggerSync.forEach((adAccountId, index) => {
                if (adAccountId) {
                    triggerAsyncSyncOfMetaAdAccount(adAccountId, index === 0)
                }
            })
        }
    }

    const handleConnectMeta = () => {
        AnalyticsService.track('connect-meta-account-from-onboarding', { metaIntegrationId: metaIntegration?._id })
        setIsMetaConnectionStarted(true)
        connectToMeta(metaIntegration)
    }

    // Meta connection completed — kick off the first sync (once). Ad-account
    // selection is intentionally NOT part of onboarding (it's configured later
    // in the dashboard); triggerSyncOnce syncs the account's first few ad
    // accounts so reporting has data without any picker friction.
    useEffect(() => {
        if (metaIntegration && isMetaConnectionStarted) {
            setIsMetaConnectionStarted(false)
            triggerSyncOnce()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metaIntegration, isMetaConnectionStarted])

    // Bounce out if this account no longer needs onboarding.
    useEffect(() => {
        if (!account?.requiresOnboarding) {
            navigate('../dashboard/intelligence/discovery', { state: { entry: 'onboarding' } })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const createInitialAiContext = async (websiteUrl) => {
        try {
            if (!websiteUrl) {
                return
            }
            const brand = {
                websiteUrl,
                marketCountries: [{ value: 'US', label: 'United State' }],
            }
            const perGoals = {
                goalMetrics: ['spend', 'purchase_value', 'roas', 'clicks_all', 'ctr'],
            }
            await Promise.all([
                API.generateAiContextBrand(brand),
                API.generateAiContextPerformanceGoals(perGoals),
            ])
        } catch (e) {
            handleError(e)
        }
    }

    // Where the user lands, driven by their answers:
    //   platform + connected ad account → Reporting (creative)
    //   platform + no ad account        → Discovery (intelligence)
    //   platform not selected           → API section
    const resolveDestination = (usage) => {
        const wantsPlatform = usage.includes('platform')
        if (wantsPlatform && metaIntegration) {
            return '../dashboard/creative/home'
        }
        if (wantsPlatform && !metaIntegration) {
            return '../dashboard/intelligence/discovery'
        }
        return '../dashboard/api'
    }

    const handleFinish = async ({ intent, intentOther, usage, website }) => {
        try {
            setIsFinishing(true)
            const resolvedWebsite = inferredDomain ? `https://${inferredDomain}` : normalizeUrl(website)
            const destination = resolveDestination(usage)

            AnalyticsService.track('onboarding-completed', {
                intent,
                intentOther,
                usage,
                connectedMeta: Boolean(metaIntegration),
                hasWebsite: Boolean(resolvedWebsite),
                destination,
            })

            const payload = { requiresOnboarding: false }
            if (resolvedWebsite) {
                payload.mainWebsite = resolvedWebsite
            }
            await API.updateAccount(account._id, payload)
            updateAccountRequiresOnboarding(account._id, false)
            createInitialAiContext(resolvedWebsite)

            navigate(destination, { state: { entry: 'onboarding' } })
        } catch (e) {
            handleError(e)
            setIsFinishing(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-auth-paper">
            <OnboardingFlow
                firstName={firstName}
                isGenericEmail={!inferredDomain}
                metaIntegration={metaIntegration}
                onConnectMeta={handleConnectMeta}
                onFinish={handleFinish}
                onEvent={(name, props) => AnalyticsService.track(name, props)}
                onLogout={logout}
                isFinishing={isFinishing}
            />
        </div>
    )
}
